import { useState } from 'react';
import { MessageSquare, User, Bot, ArrowRight } from 'lucide-react';

interface DemoConversationProps {
  onStartFree?: () => void;
}

const DEMO_CONVERSATION = [
  {
    type: 'user',
    message: "I'm thinking about switching careers but I'm scared I'll regret it. Everyone says I have a good job."
  },
  {
    type: 'coach',
    message: "Let me ask you this - what does 'good job' actually mean to you? Because from the outside, a job can look perfect while slowly draining you. What's making you consider the switch?"
  },
  {
    type: 'user',
    message: "I guess I'm just bored? Like I'm not learning anything new and I feel stuck."
  },
  {
    type: 'coach',
    message: "Bored isn't the same as stuck. You're learning that staying where you are has a cost - your growth. Here's the thing: fear of regret cuts both ways. What if you regret NOT switching? Let's break this down. What would you actually need to see or feel to know switching was worth it?"
  }
];

export function DemoConversation({ onStartFree }: DemoConversationProps) {
  const [visibleMessages, setVisibleMessages] = useState(2);

  const showMore = () => {
    if (visibleMessages < DEMO_CONVERSATION.length) {
      setVisibleMessages(visibleMessages + 1);
    }
  };

  const isFullyExpanded = visibleMessages >= DEMO_CONVERSATION.length;

  return (
    <section className="py-16 sm:py-24 bg-gradient-to-b from-white to-slate-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-coral-50 rounded-full text-coral-600 text-sm font-medium mb-6">
            <MessageSquare className="h-4 w-4" />
            <span>See it in action</span>
          </div>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
            Real conversation, real guidance
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            This is what coaching with VZMO actually looks like. No fluff, no generic advice.
          </p>
        </div>

        <div className="bg-white rounded-3xl shadow-xl border border-slate-200 overflow-hidden">
          <div className="bg-gradient-to-r from-slate-50 to-slate-100 px-6 py-4 border-b border-slate-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-coral-500 flex items-center justify-center">
                <Bot className="h-5 w-5 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900">Your VZMO Coach</h3>
                <p className="text-sm text-slate-500">Always available</p>
              </div>
            </div>
          </div>

          <div className="p-6 space-y-4 max-h-[600px] overflow-y-auto">
            {DEMO_CONVERSATION.slice(0, visibleMessages).map((msg, idx) => (
              <div
                key={idx}
                className={`flex gap-3 ${msg.type === 'user' ? 'flex-row-reverse' : 'flex-row'} animate-fadeIn`}
                style={{ animationDelay: `${idx * 100}ms` }}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                  msg.type === 'user'
                    ? 'bg-slate-200'
                    : 'bg-gradient-to-br from-coral-500 to-coral-600'
                }`}>
                  {msg.type === 'user' ? (
                    <User className="h-4 w-4 text-slate-600" />
                  ) : (
                    <Bot className="h-4 w-4 text-white" />
                  )}
                </div>
                <div className={`flex-1 ${msg.type === 'user' ? 'flex justify-end' : ''}`}>
                  <div className={`inline-block px-4 py-3 rounded-2xl max-w-[85%] ${
                    msg.type === 'user'
                      ? 'bg-slate-100 text-slate-900'
                      : 'bg-gradient-to-br from-coral-50 to-orange-50 text-slate-900 border border-coral-100'
                  }`}>
                    <p className="text-sm leading-relaxed">{msg.message}</p>
                  </div>
                </div>
              </div>
            ))}

            {!isFullyExpanded && (
              <div className="flex justify-center pt-4">
                <button
                  onClick={showMore}
                  className="group flex items-center gap-2 px-5 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-full text-sm font-medium transition-all"
                >
                  <span>See how coach responds</span>
                  <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            )}
          </div>

          {isFullyExpanded && onStartFree && (
            <div className="border-t border-slate-200 bg-slate-50 px-6 py-6 text-center">
              <p className="text-slate-600 mb-4">Start your own conversation right now</p>
              <button
                onClick={onStartFree}
                className="group inline-flex items-center justify-center px-8 py-4 bg-slate-900 text-white font-semibold text-lg rounded-full hover:bg-slate-800 transition-all duration-200 shadow-lg hover:shadow-xl"
              >
                Get started free
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform duration-200" />
              </button>
              <p className="text-sm text-slate-500 mt-3">No credit card required</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
